import React, { useEffect, useState } from "react";
import axios from "../../api/axiosInstance";

function formatDate(dateValue) {
  if (!dateValue) return "Not applied yet";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "Invalid date";
  return parsed.toLocaleString();
}

export default function OnboardingRoomCategoriesAdmin() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [applyingBusinessUnitId, setApplyingBusinessUnitId] = useState(null);
  const [feedback, setFeedback] = useState("");

  const loadDrafts = async () => {
    setLoading(true);
    setFeedback("");

    try {
      const response = await axios.get("/api/onboarding/admin/room-categories/drafts");
      setDrafts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("[Admin][Onboarding] Error loading drafts:", error);
      setFeedback("Could not load onboarding drafts.");
    } finally {
      setLoading(false);
    }
  };

  const loadDraftDetail = async (businessUnitId) => {
    setSelectedBusinessUnitId(businessUnitId);
    setDetailLoading(true);
    setFeedback("");

    try {
      const response = await axios.get(`/api/onboarding/admin/room-categories/${businessUnitId}`);
      setSelectedDraft(response.data);
    } catch (error) {
      console.error("[Admin][Onboarding] Error loading draft detail:", error);
      setSelectedDraft(null);
      setFeedback("Could not load the selected draft.");
    } finally {
      setDetailLoading(false);
    }
  };

  const applyDraft = async (businessUnitId, businessUnitName) => {
    const confirmed = window.confirm(
      `Apply the onboarding room categories draft for ${businessUnitName}? This will upsert data into room_categories.`
    );

    if (!confirmed) return;

    setApplyingBusinessUnitId(businessUnitId);
    setFeedback("");

    try {
      const response = await axios.post(`/api/onboarding/admin/room-categories/${businessUnitId}/apply`);
      const result = response.data?.result || {};
      setFeedback(
        `Draft applied for ${businessUnitName}. Created: ${result.created || 0}. Updated: ${result.updated || 0}.`
      );
      await loadDrafts();
      await loadDraftDetail(businessUnitId);
    } catch (error) {
      console.error("[Admin][Onboarding] Error applying draft:", error);
      setFeedback(error?.response?.data?.error || "Could not apply the selected draft.");
    } finally {
      setApplyingBusinessUnitId(null);
    }
  };

  useEffect(() => {
    loadDrafts();
  }, []);

  return (
    <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "18px" }}>
      <div
        style={{
          borderRadius: "22px",
          padding: "22px",
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "radial-gradient(780px 240px at 0% 0%, rgba(92,191,255,0.12), transparent 60%), rgba(10,15,28,0.9)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.12em", color: "#8ea0bc" }}>
          Admin apply area
        </div>
        <h2 style={{ color: "#fff", margin: "8px 0 10px" }}>Onboarding Room Categories</h2>
        <p style={{ color: "#b9c3d7", maxWidth: "820px", margin: 0, lineHeight: 1.6 }}>
          Aqui revisas los borradores guardados desde el modulo de onboarding y los vuelcas a{" "}
          <strong>`room_categories`</strong> con un clic. El apply hace upsert seguro y sincroniza tambien
          la disponibilidad diaria base.
        </p>
      </div>

      {feedback ? (
        <div
          style={{
            borderRadius: "14px",
            padding: "12px 14px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: feedback.toLowerCase().includes("could not") ? "#542929" : "#244428",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {feedback}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 420px) minmax(0, 1fr)",
          gap: "18px",
        }}
      >
        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
            <div>
              <h3 style={{ color: "#fff", margin: 0 }}>Drafts</h3>
              <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>Business units con borrador pendiente o aplicado.</p>
            </div>
            <button
              type="button"
              onClick={loadDrafts}
              disabled={loading}
              style={{
                borderRadius: "12px",
                border: "none",
                padding: "10px 14px",
                background: "#5cbfff",
                color: "#08111e",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {loading ? (
              <div style={{ color: "#9db0cf" }}>Loading onboarding drafts...</div>
            ) : drafts.length === 0 ? (
              <div style={{ color: "#9db0cf" }}>No room categories drafts available yet.</div>
            ) : (
              drafts.map((draft) => {
                const isSelected = Number(selectedBusinessUnitId) === Number(draft.business_unit_id);
                return (
                  <article
                    key={draft.business_unit_id}
                    style={{
                      borderRadius: "18px",
                      padding: "16px",
                      border: `1px solid ${isSelected ? "rgba(92,191,255,0.45)" : "rgba(255,255,255,0.08)"}`,
                      background: isSelected ? "rgba(92,191,255,0.08)" : "rgba(255,255,255,0.03)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                      <div>
                        <h4 style={{ color: "#fff", margin: 0 }}>{draft.business_unit_name}</h4>
                        <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>{draft.rows_total} staged rows</p>
                      </div>
                      <div style={{ color: "#e7edf8", fontWeight: 700 }}>{draft.linked_rows_total} linked</div>
                    </div>

                    <div style={{ marginTop: "12px", color: "#9db0cf", fontSize: "0.9rem", lineHeight: 1.5 }}>
                      <div>Last save: {formatDate(draft.updated_at)}</div>
                      <div>Last apply: {formatDate(draft.applied_at)}</div>
                    </div>

                    <div style={{ marginTop: "14px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => loadDraftDetail(draft.business_unit_id)}
                        style={{
                          borderRadius: "12px",
                          border: "none",
                          padding: "10px 14px",
                          background: "#1b2c43",
                          color: "#fff",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => applyDraft(draft.business_unit_id, draft.business_unit_name)}
                        disabled={applyingBusinessUnitId === draft.business_unit_id}
                        style={{
                          borderRadius: "12px",
                          border: "none",
                          padding: "10px 14px",
                          background: "#ff975c",
                          color: "#08111e",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {applyingBusinessUnitId === draft.business_unit_id ? "Applying..." : "Apply to live"}
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          {!selectedBusinessUnitId ? (
            <div style={{ color: "#9db0cf" }}>Select a draft to preview its staged room categories.</div>
          ) : detailLoading ? (
            <div style={{ color: "#9db0cf" }}>Loading draft detail...</div>
          ) : !selectedDraft ? (
            <div style={{ color: "#9db0cf" }}>No draft detail loaded.</div>
          ) : (
            <>
              <div style={{ marginBottom: "16px" }}>
                <h3 style={{ color: "#fff", margin: 0 }}>{selectedDraft.session?.business_unit_name}</h3>
                <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>
                  Last save: {formatDate(selectedDraft.session?.updated_at)}. Last apply:{" "}
                  {formatDate(selectedDraft.session?.applied_at)}.
                </p>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff" }}>
                  <thead>
                    <tr style={{ textAlign: "left", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                      <th style={{ padding: "10px 8px" }}>Name</th>
                      <th style={{ padding: "10px 8px" }}>Description</th>
                      <th style={{ padding: "10px 8px" }}>Adults</th>
                      <th style={{ padding: "10px 8px" }}>Children</th>
                      <th style={{ padding: "10px 8px" }}>Occupancy</th>
                      <th style={{ padding: "10px 8px" }}>Linked live row</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedDraft.rows || []).map((row) => (
                      <tr key={row.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                        <td style={{ padding: "10px 8px", fontWeight: 700 }}>{row.name}</td>
                        <td style={{ padding: "10px 8px", color: "#c3cee0" }}>{row.description || "-"}</td>
                        <td style={{ padding: "10px 8px" }}>{row.max_adults}</td>
                        <td style={{ padding: "10px 8px" }}>{row.max_children}</td>
                        <td style={{ padding: "10px 8px" }}>{row.max_occupancy ?? "-"}</td>
                        <td style={{ padding: "10px 8px" }}>{row.applied_room_category_id || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
